import {type Line} from '../../../util/plotHelpers/types';
import {type FacetData, getFacetedData} from './getFacetedData';
import {getSeriesEntries} from './getSeriesEntries';
import {getUPlotTelemetryContext} from './uPlotTelemetry';

/** Prepare each line once so the main and highlighted layers share its facets. */
export function prepareUPlotData(lines: Line[], xLogScale?: boolean) {
  const entries = getSeriesEntries(lines);
  return {...entries, data: getFacetedData(entries, xLogScale)};
}

export type PreparedUPlotData = ReturnType<typeof prepareUPlotData>;

const PREPARATION_SAMPLE_RATE = 0.15;

export type UPlotPreparationMeasurement = {
  startTime: number;
  duration: number;
  context: ReturnType<typeof getUPlotTelemetryContext> & {
    uPlotMode: 'faceted';
    xLogScale: boolean;
    sampleRate: number;
    samplingUnit: 'committed-panel-preparation';
  };
};

/** Time the owning panel's data preparation; drawing layers only read its result. */
export function getProfiledUPlotData(
  lines: Line[],
  xLogScale?: boolean
): {
  preparedData: PreparedUPlotData;
  measurement: UPlotPreparationMeasurement | undefined;
} {
  const sampled = Math.random() < PREPARATION_SAMPLE_RATE;
  const startMark = sampled ? performance.now() : 0;
  const preparedData = prepareUPlotData(lines, xLogScale);
  const duration = sampled ? performance.now() - startMark : 0;
  if (!sampled) {
    return {preparedData, measurement: undefined};
  }
  const hasPoints = preparedData.data.some(
    (facet, index) => index > 0 && (facet as FacetData)[0].length > 0
  );
  if (!hasPoints) {
    return {preparedData, measurement: undefined};
  }
  let context: UPlotPreparationMeasurement['context'];
  try {
    context = {
      ...getUPlotTelemetryContext(lines, preparedData.seriesEntries),
      uPlotMode: 'faceted',
      xLogScale: xLogScale === true,
      sampleRate: PREPARATION_SAMPLE_RATE,
      samplingUnit: 'committed-panel-preparation',
    };
  } catch {
    // Optional telemetry must not interrupt a successfully prepared chart.
    return {preparedData, measurement: undefined};
  }
  return {
    preparedData,
    measurement: {
      startTime: performance.timeOrigin + startMark,
      duration,
      context,
    },
  };
}
